import React from "react";
import { Link } from "react-router-dom";
import { Clock, Lock, PlayCircle, Bell } from "lucide-react";

const DIFFICULTY_TONE = {
  Beginner: "text-[#10B981] border-[#10B981]/30",
  Intermediate: "text-[#3B82F6] border-[#3B82F6]/30",
  Advanced: "text-[#F59E0B] border-[#F59E0B]/30",
  Expert: "text-[#FF4343] border-[#FF4343]/30",
};

const CATEGORY_LABEL = {
  marketing: "Marketing",
  sales: "Sales",
  service: "Service",
  operations: "Operations",
  "executive-mindset": "Executive Mindset",
};

export default function CourseCard({ course, featured }) {
  const tone = DIFFICULTY_TONE[course.difficulty] || "text-white border-white/30";
  const comingSoon = !!course.is_coming_soon;
  const linkTo = comingSoon
    ? `/courses/${course.slug}/coming-soon`
    : `/courses/${course.slug}`;
  return (
    <Link
      to={linkTo}
      className={`course-card group relative flex flex-col bg-[#060A24] border border-white/5 overflow-hidden ${
        featured || course.is_featured ? "featured-course-ring" : ""
      }`}
      data-testid={`course-card-${course.slug}`}
      data-coming-soon={comingSoon ? "true" : "false"}
    >
      {comingSoon ? (
        <div
          className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] font-black uppercase rounded-sm border border-amber-400/70 bg-amber-500/20 text-amber-200 backdrop-blur-sm"
          data-testid={`coming-soon-badge-${course.slug}`}
        >
          <Clock size={10} /> Coming Soon
        </div>
      ) : (
        (featured || course.is_featured) && (
          <div className="absolute top-3 left-3 z-10 bg-[#FF4343] text-white text-[10px] tracking-[0.18em] font-bold px-2.5 py-1">
            FEATURED
          </div>
        )
      )}
      {!comingSoon && course.membership_required === "executive" && (
        <div className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur-sm text-white text-[10px] tracking-[0.18em] font-bold px-2.5 py-1 flex items-center gap-1">
          <Lock size={10} /> EXECUTIVE
        </div>
      )}
      <div className="relative aspect-[16/10] overflow-hidden bg-[#090E33]">
        <img
          src={course.thumbnail_url}
          alt={course.title}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
            comingSoon
              ? "opacity-50 grayscale group-hover:opacity-60"
              : "opacity-80 group-hover:opacity-100 group-hover:scale-105"
          }`}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060A24] via-[#060A24]/40 to-transparent" />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.18em] font-semibold">
          <span className="text-white/60">{CATEGORY_LABEL[course.category] || course.category}</span>
          <span className={`px-2 py-0.5 border ${tone}`}>{course.difficulty.toUpperCase()}</span>
        </div>
        <h3
          className={`font-black text-lg leading-tight tracking-tight mb-2 transition-colors ${
            comingSoon ? "text-white/80 group-hover:text-amber-300" : "text-white group-hover:text-[#FF4343]"
          }`}
        >
          {course.title}
        </h3>
        <p className="text-white/60 text-sm leading-relaxed line-clamp-2 flex-1">{course.subtitle}</p>
        <div className="mt-5 flex items-center justify-between text-xs text-white/50">
          <span className="flex items-center gap-1.5">
            <Clock size={12} />
            {course.estimated_duration_hours}h
          </span>
          {comingSoon ? (
            <span className="flex items-center gap-1.5 text-amber-300 font-black tracking-wider">
              <Bell size={13} /> JOIN WAITLIST
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-[#FF4343] font-bold">
              <PlayCircle size={14} />
              START FREE LESSON
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
